import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { Button as ButtonUnstyled } from '@mui/base/Button';
import { useId } from 'react';
import { Box, Stack } from 'src/components-v2/Layout';
import { useTrack } from 'src/contexts/AnalyticsContext';
import { useIsDarkMode } from 'src/hooks/useIsDarkMode';
import { Events } from 'src/lib/trackingEvents';
import styled from 'threads5/themes/styled';
import { IBtnProps } from './Btn.types';
import { useLocalizedHref } from './useLocalizedHref';

// `sx` is processed by MUI styled into the emotion class; without listing it
// here the custom shouldForwardProp also leaks the raw object onto the DOM
// element as an invalid `sx="[object Object]"` attribute.
const forbiddenProps = new Set(['rightIcon', 'leftIcon', 'sx']);

const StyledTertiaryButton = styled(ButtonUnstyled, {
  shouldForwardProp: (prop: string) => {
    return !forbiddenProps.has(prop);
  },
})(({ theme }) => {
  return {
    alignItems: 'center',
    border: 'none',
    borderRadius: '100px !important',
    display: 'inline-flex',
    gap: 1,
    fontFamily: theme.typography.sans,
    fontWeight: 600,
    letterSpacing: '-0.75px',
    lineHeight: '130%',
    color: `${theme.palette['web3/component/button/tertiary/text']}`,
    paddingRight: '6px',
    outline: 'none',
    [theme.breakpoints.up('xs')]: {
      fontSize: '1.8rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '2.0rem',
    },
    '&:focus-visible': {
      outlineWidth: '2px',
      outlineColor: theme.palette['web3/component/button/outline'],
      outlineStyle: 'solid',
      outlineOffset: '3px',
    },
    '&:hover, &:focus': {
      color: `${theme.palette['web3/component/button/tertiary/text']}`,
    },
    '& svg': {
      borderRadius: '100px',
    },
    '&:hover svg': {
      fill: theme.palette[
        'web3/component/button/tertiary/icon-container/interacted-fill'
      ],
      boxShadow:
        theme.palette['web3/component/button/tertiary/icon/hovered-glow'],
      '& path.arrow': {
        fill: theme.palette[
          'web3/component/button/tertiary/icon/fill-interacted'
        ],
      },
      '& path.circle': {
        stroke:
          theme.palette['web3/component/button/tertiary/icon/fill-interacted'],
      },
    },
    '&:active svg': {
      boxShadow: 'none',
      transition: 'transform 0.1s ease',
      transform: 'scale(0.96)',
    },
  };
});

const BtnTertiary = ({
  children,
  isInverted,
  trackingId,
  variant,
  sys,
  backgroundColor,
  iconName,
  iconSize,
  defaultGap,
  ...props
}: IBtnProps) => {
  const isDark = useIsDarkMode();
  const track = useTrack();
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  const { href, ...buttonProps } = props;
  const localizedHref = useLocalizedHref(href);

  // generate a unique ID for the button svg
  // this cleans up dup ID errors, etc
  const uID = useId();
  const circlePathId = uID + '-circle';
  const arrowPathId = uID + '-arrow';

  const handleClick = (event) => {
    if (trackingId) {
      track({
        type: Events.BUTTON_CLICKED,
        payload: { trackingId, event, currentTarget: event.currentTarget },
      });
    }
    if (buttonProps.onClick && !href) {
      buttonProps.onClick(event);
    }
  };

  return (
    <StyledTertiaryButton
      {...buttonProps}
      href={localizedHref}
      slots={{ root: href ? 'a' : 'button' }}
      onClick={handleClick}
      {...inspectorProps({ fieldId: 'text' })}
    >
      <Stack component='span' direction='row' alignItems='center' gap={1}>
        <svg
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          xmlnsXlink='http://www.w3.org/1999/xlink'
        >
          <defs>
            <linearGradient
              id={circlePathId}
              x1='16.859'
              y1='600.296'
              x2='43.141'
              y2='621.463'
              gradientTransform='translate(-18 622.88) scale(1 -1)'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor={isDark ? '#F9DBFF' : '#671877'} />
              <stop offset='0.5' stopColor={isDark ? '#E6E6FF' : '#2B2B82'} />
              <stop offset='1' stopColor={isDark ? '#CCF8FF' : '#043C67'} />
            </linearGradient>
            <linearGradient
              id={arrowPathId}
              x1='24.215'
              y1='605.565'
              x2='35.485'
              y2='616.446'
              xlinkHref={`#${circlePathId}`}
            >
              <stop stopColor={isDark ? '#F9DBFF' : '#671877'} />
              <stop offset='0.5' stopColor={isDark ? '#E6E6FF' : '#2B2B82'} />
              <stop offset='1' stopColor={isDark ? '#CCF8FF' : '#043C67'} />
            </linearGradient>
          </defs>
          <path
            className='circle'
            d='M 1 12 C 1 5.925 5.925 1 12 1 C 18.075 1 23 5.925 23 12 C 23 18.075 18.075 23 12 23 C 5.925 23 1 18.075 1 12 Z'
            stroke={`url(#${circlePathId})`}
            strokeWidth='1.5px'
            fill='none'
          />
          <path
            className='arrow'
            d='M 15.008 11.284 L 11.942 8.212 L 12.952 7.2 L 17.742 12 L 12.952 16.8 L 11.942 15.788 L 15.008 12.715 L 6.257 12.715 L 6.257 11.284 L 15.008 11.284 Z'
            fill={`url(#${arrowPathId})`}
          />
        </svg>
        <Box component='span'>{children}</Box>
      </Stack>
    </StyledTertiaryButton>
  );
};

export default BtnTertiary;
